import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyProfile, fetchPublicCompanyProfile } from '@/services/companyProfile';

const fallbackCompanyProfile: CompanyProfile = {
  companyName: '富聆聽健康',
  servicePhone: '0800-123-456',
  servicePhoneNote: '免付費',
  serviceHours: '週一至週五 09:00 - 18:00',
  serviceEmail: 'service@fuling.com',
  addressCity: '台北市',
  addressDistrict: '信義區',
  addressPostalCode: '110',
  addressLine: '松高路 88 號',
  contactPhone: null,
  smsPhone: null,
  copyright: null,
  lineId: '@fulinghealth',
  lineUrl: null,
  facebookUrl: null,
  instagramUrl: null,
  youtubeUrl: null,
};

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>(fallbackCompanyProfile);

  useEffect(() => {
    let alive = true;
    fetchPublicCompanyProfile().then((profile) => {
      if (alive && profile) setCompanyProfile(profile);
    });
    return () => {
      alive = false;
    };
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitStatus('loading');
    setSubmitError('');
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ Email: email, SourcePage: 'Footer' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        throw new Error(data.message || '訂閱失敗，請稍後再試。');
      }
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus('idle'), 3500);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '訂閱失敗，請稍後再試。');
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3500);
    }
  };

  const linkGroups = [
    {
      title: '關於我們',
      links: [
        { label: '品牌故事', path: '/about' },
        { label: '專業團隊', path: '/about' },
        { label: '服務據點', path: '/contact' },
        { label: '共聽生活協會', path: '/association' },
      ],
    },
    {
      title: '產品系列',
      links: [
        { label: '耳內式助聽器', path: '/products' },
        { label: '耳掛式助聽器', path: '/products' },
        { label: '充電式助聽器', path: '/products' },
        { label: '全系列產品', path: '/products' },
      ],
    },
    {
      title: '服務項目',
      links: [
        { label: '聽力檢測預約', path: '/booking' },
        { label: '聽力知識庫', path: '/knowledge' },
        { label: '常見問題', path: '/faq' },
        { label: '訂單查詢', path: '/order-lookup' },
        { label: '聯絡我們', path: '/contact' },
      ],
    },
    {
      title: '相關資源',
      links: [
        { label: '聽力保健指南', path: '/knowledge' },
        { label: '助聽器保養', path: '/knowledge' },
        { label: '噪音防護知識', path: '/knowledge' },
        { label: '亞洲峰會', path: '/asia-summit' },
      ],
    },
  ];

  const socialLinks = [
    { icon: 'ri-facebook-fill', label: 'Facebook', url: companyProfile.facebookUrl },
    { icon: 'ri-line-fill', label: 'LINE', url: companyProfile.lineUrl },
    { icon: 'ri-instagram-fill', label: 'Instagram', url: companyProfile.instagramUrl },
    { icon: 'ri-youtube-fill', label: 'YouTube', url: companyProfile.youtubeUrl },
  ].filter((social) => Boolean(social.url));

  const phoneDigits = companyProfile.servicePhone.replace(/[^\d+]/g, '');
  const companyAddress = `${companyProfile.addressCity}${companyProfile.addressDistrict}${companyProfile.addressLine}`;

  return (
    <footer>
      {/* Decorative wave transition */}
      <div className="relative -mb-px overflow-hidden leading-none">
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full h-10 md:h-14"
        >
          <path
            d="M0 80L60 74C120 68 240 56 360 50C480 44 600 44 720 47C840 50 960 56 1080 57C1200 58 1320 54 1380 52L1440 50V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V80Z"
            className="fill-primary-950"
          />
        </svg>
      </div>

      {/* Main footer */}
      <div className="bg-primary-950 relative overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--accent-500) / 0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative px-4 md:px-10 lg:px-16">

          {/* ── Newsletter Section ── */}
          <div className="border-b border-foreground-100/10">
            <div className="py-8 md:py-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold" style={{ color: 'oklch(var(--accent-500))' }}>
                  訂閱最新資訊
                </h3>
                <p className="mt-1.5 text-sm text-foreground-300/85 leading-relaxed">
                  掌握聽力保健知識、最新產品資訊與限時優惠，每月精選內容直送信箱
                </p>
              </div>
              <form
                data-readdy-form=""
                id="footer-newsletter"
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-2.5 w-full lg:w-auto lg:min-w-[420px]"
              >
                <div className="flex-1 relative">
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="輸入您的電子郵件"
                    className="w-full px-4 py-3 bg-primary-900/80 border border-foreground-100/15 rounded-lg text-sm text-foreground-100 placeholder:text-foreground-400/50 focus:outline-none focus:border-accent-500/60 focus:ring-1 focus:ring-accent-500/30 transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitStatus === 'loading' || submitStatus === 'success'}
                  className="px-6 py-3 bg-accent-500 text-primary-950 rounded-lg text-sm font-semibold whitespace-nowrap hover:bg-accent-400 disabled:opacity-60 transition-all duration-200 cursor-pointer"
                >
                  {submitStatus === 'loading' ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      處理中
                    </span>
                  ) : submitStatus === 'success' ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-check-line" />
                      已訂閱
                    </span>
                  ) : submitStatus === 'error' ? (
                    '請稍後再試'
                  ) : (
                    '立即訂閱'
                  )}
                </button>
              </form>
              {submitStatus === 'success' && (
                <p className="text-xs text-accent-400 mt-1">訂閱成功！感謝您的關注。</p>
              )}
              {submitStatus === 'error' && submitError && (
                <p className="text-xs text-red-400 mt-1">{submitError}</p>
              )}
            </div>
          </div>

          {/* ── Link Columns ── */}
          <div className="py-10 md:py-14 grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10">
            {/* Brand + Contact Column */}
            <div className="col-span-2">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="inline-flex items-center gap-2 mb-5"
              >
                <span className="text-2xl md:text-3xl font-extrabold text-foreground-50">
                  富聆聽
                </span>
                <span className="text-2xl md:text-3xl font-extrabold" style={{ color: 'oklch(var(--accent-500))' }}>
                  健康
                </span>
              </a>
              <p className="text-sm text-foreground-300/85 leading-relaxed mb-6 max-w-sm">
                專業聽力照護與助聽器選配，守護您的聆聽品質與生活美好
              </p>

              {/* Divider */}
              <div
                className="w-12 h-px mb-6 rounded-full"
                style={{ backgroundColor: 'oklch(var(--accent-500) / 0.4)' }}
              />

              {/* Contact info */}
              <div className="space-y-4">
                <a
                  href={`tel:${phoneDigits}`}
                  className="flex items-center gap-3 text-sm text-foreground-300 hover:text-accent-400 transition-colors group"
                >
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100/6 group-hover:bg-accent-500/15 transition-colors">
                    <i className="ri-phone-line group-hover:text-accent-400 transition-colors" style={{ color: 'oklch(var(--accent-500) / 0.8)' }} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400/60 mb-0.5">客服專線</p>
                    <p className="font-medium">
                      {companyProfile.servicePhone}
                      {companyProfile.servicePhoneNote ? `（${companyProfile.servicePhoneNote}）` : ''}
                    </p>
                  </div>
                </a>
                <a
                  href={`mailto:${companyProfile.serviceEmail}`}
                  className="flex items-center gap-3 text-sm text-foreground-300 hover:text-accent-400 transition-colors group"
                >
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100/6 group-hover:bg-accent-500/15 transition-colors">
                    <i className="ri-mail-line group-hover:text-accent-400 transition-colors" style={{ color: 'oklch(var(--accent-500) / 0.8)' }} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400/60 mb-0.5">電子郵件</p>
                    <p className="font-medium">{companyProfile.serviceEmail}</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 text-sm text-foreground-300">
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100/6">
                    <i className="ri-map-pin-line" style={{ color: 'oklch(var(--accent-500) / 0.8)' }} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400/60 mb-0.5">公司地址</p>
                    <p className="font-medium">{companyAddress}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground-300">
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100/6">
                    <i className="ri-time-line" style={{ color: 'oklch(var(--accent-500) / 0.8)' }} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400/60 mb-0.5">客服時間</p>
                    <p className="font-medium">{companyProfile.serviceHours}</p>
                  </div>
                </div>
                {companyProfile.lineId && (
                  <a
                    href={companyProfile.lineUrl || undefined}
                    onClick={(e) => { if (!companyProfile.lineUrl) e.preventDefault(); }}
                    target={companyProfile.lineUrl ? '_blank' : undefined}
                    rel="noreferrer"
                    className="flex items-center gap-3 text-sm text-foreground-300 hover:text-accent-400 transition-colors group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-foreground-100/6 group-hover:bg-accent-500/15 transition-colors">
                      <i className="ri-line-fill group-hover:text-accent-400 transition-colors" style={{ color: 'oklch(var(--accent-500) / 0.8)' }} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400/60 mb-0.5">LINE 官方帳號</p>
                      <p className="font-medium">{companyProfile.lineId}</p>
                    </div>
                  </a>
                )}
              </div>
            </div>

            {/* Link Groups */}
            {linkGroups.map((group) => (
              <div key={group.title}>
                <h4 className="text-sm md:text-base font-bold mb-4 text-foreground-50">
                  {group.title}
                </h4>
                <div
                  className="w-8 h-[2px] mb-4 rounded-full"
                  style={{ backgroundColor: 'oklch(var(--accent-500) / 0.6)' }}
                />
                <ul className="space-y-3">
                  {group.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.path}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(link.path);
                        }}
                        className="group flex items-center gap-2 text-sm text-foreground-300 hover:text-accent-400 transition-colors duration-200"
                      >
                        <span className="w-0 h-[2px] rounded-full transition-all duration-200 group-hover:w-2.5 flex-shrink-0" style={{ backgroundColor: 'oklch(var(--accent-500))' }} />
                        <span>{link.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <div className="border-t border-foreground-100/10">
          <div className="px-4 md:px-10 lg:px-16 py-5 md:py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Copyright + legal links */}
            <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 text-sm text-foreground-400/55">
              <span>{companyProfile.copyright || `© 2026 ${companyProfile.companyName}`}</span>
              <span className="hidden sm:inline text-foreground-500/30">|</span>
              <div className="flex items-center gap-3">
                <a
                  href="/page/privacy-policy"
                  onClick={(e) => { e.preventDefault(); navigate('/page/privacy-policy'); }}
                  className="hover:text-accent-400 transition-colors"
                >
                  隱私權政策
                </a>
                <a
                  href="/page/terms-of-service"
                  onClick={(e) => { e.preventDefault(); navigate('/page/terms-of-service'); }}
                  className="hover:text-accent-400 transition-colors"
                >
                  使用條款
                </a>
              </div>
            </div>

            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-100/8 hover:bg-accent-500 transition-all duration-300 cursor-pointer group"
                  >
                    <i
                      className={`${social.icon} text-sm text-foreground-300/70 group-hover:text-primary-950 transition-colors`}
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}